import { useState, useCallback, useEffect } from "react";
import { Terminal, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Sidebar } from "@/components/layout/Sidebar";
import { BottomNav } from "@/components/layout/BottomNav";
import { SessionSidebar } from "@/components/sessions/SessionSidebar";
import { SessionHeader } from "@/components/sessions/SessionHeader";
import { SessionTerminal } from "@/components/sessions/SessionTerminal";
import { SessionInput } from "@/components/sessions/SessionInput";
import { NewSessionSheet } from "@/components/sessions/NewSessionSheet";
import { PullToRefreshIndicator } from "@/components/layout/PullToRefreshIndicator";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useSessions } from "@/hooks/useSessions";
import { useLiveSession } from "@/hooks/useLiveSession";
import type { LiveSessionProvider } from "@/lib/types";

const SESSION_STORAGE_KEY = "ho-active-session-id";

export default function Sessions() {
  const [activeSessionId, setActiveSessionIdRaw] = useState<string | null>(
    () => localStorage.getItem(SESSION_STORAGE_KEY)
  );
  const [showNewSheet, setShowNewSheet] = useState(false);

  // Persist activeSessionId to localStorage
  const setActiveSessionId = useCallback((id: string | null) => {
    setActiveSessionIdRaw(id);
    if (id) {
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, []);

  const queryClient = useQueryClient();
  const { sessions, isLoading, createSession, reactivateSession, deleteSession } = useSessions();

  // Auto-select: if stored session is gone/ended, pick the first active session instead
  useEffect(() => {
    if (!sessions.length || isLoading) return;

    if (activeSessionId) {
      const stored = sessions.find((s) => s.id === activeSessionId);
      // If the stored session doesn't exist in our list anymore, clear it
      if (!stored) {
        const firstActive = sessions.find(
          (s) => s.status === "active" || s.status === "starting"
        );
        setActiveSessionId(firstActive?.id ?? null);
      }
    } else {
      // No active selection — pick first active session, or fall back to most recent
      const firstActive = sessions.find(
        (s) => s.status === "active" || s.status === "starting"
      );
      const fallback = firstActive ?? sessions[0];
      if (fallback) {
        setActiveSessionId(fallback.id);
      }
    }
  }, [sessions, isLoading, activeSessionId, setActiveSessionId]);

  const refreshSessions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
  }, [queryClient]);

  const { setRef: setPullRef, pullDistance, isRefreshing, isPastThreshold } =
    usePullToRefresh({ onRefresh: refreshSessions });

  const {
    messages,
    status,
    isAgentWorking,
    sendMessage,
    endSession: endLiveSession,
  } = useLiveSession(activeSessionId);

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  // Merge local realtime status with the session object for the header
  const sessionForHeader = activeSession
    ? { ...activeSession, status }
    : null;

  const isSessionDead = status === "ended" || status === "error";

  const handleCreate = async (
    provider: LiveSessionProvider,
    projectId?: string
  ) => {
    try {
      const session = await createSession.mutateAsync({
        provider,
        project_id: projectId ?? null,
      });
      setActiveSessionId(session.id);
      setShowNewSheet(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create session"
      );
    }
  };

  const handleReactivate = async (sessionId: string) => {
    try {
      await reactivateSession.mutateAsync(sessionId);
      setActiveSessionId(sessionId);
    } catch {
      toast.error("Failed to reactivate session");
    }
  };

  const handleEnd = async () => {
    if (!activeSessionId) return;
    try {
      // Only broadcast end_session — the agent-runner handles the full DB teardown
      // (status update, ended_at, metadata merge). The frontend picks up the
      // status change via the realtime postgres_changes subscription.
      await endLiveSession();
    } catch {
      toast.error("Failed to end session");
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      await deleteSession.mutateAsync(sessionId);
      // If we deleted the active session, clear selection
      if (sessionId === activeSessionId) {
        setActiveSessionId(null);
      }
      toast("Session deleted");
    } catch {
      toast.error("Failed to delete session");
    }
  };

  const handleSend = (content: string, images?: Array<{ base64: string; mediaType: string; name: string }>) => {
    void (async () => {
      // If session is dead, reactivate it first — the agent-runner will inject
      // chat history (including this new message) as context when it restarts
      if (isSessionDead && activeSessionId) {
        try {
          await reactivateSession.mutateAsync(activeSessionId);
        } catch {
          toast.error("Failed to reactivate session");
          return;
        }
        // Pass existing messages as context so the agent runner can restore history
        const contextMessages = messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.content }));
        sendMessage(content, images, contextMessages);
      } else {
        sendMessage(content, images);
      }
    })();
  };

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100">
      {/* App Sidebar (desktop) */}
      <Sidebar />

      {/* Session Sidebar (desktop) */}
      <div className="hidden sm:block" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <SessionSidebar
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelect={(id) => setActiveSessionId(id)}
          onNew={() => setShowNewSheet(true)}
          onSessionEnded={refreshSessions}
          onReactivate={handleReactivate}
        />
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 pb-16 sm:pb-0" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        {/* Mobile top bar — scrollable session chips */}
        <div className="sm:hidden border-b border-zinc-800 bg-zinc-900">
          <div className="flex items-center gap-2 px-3 py-2">
            <div
              className="flex-1 flex items-center gap-1.5 overflow-x-auto"
              style={{ scrollbarWidth: "none" }}
            >
              {sessions.length === 0 && (
                <span className="text-xs text-zinc-500 shrink-0">No sessions</span>
              )}
              {sessions.map((session) => {
                const isLive = session.status === "active" || session.status === "starting";
                const isSelected = session.id === activeSessionId;
                const title = session.title || session.repo || "Untitled";
                return (
                  <button
                    key={session.id}
                    onClick={() => setActiveSessionId(session.id)}
                    className={cn(
                      "flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full text-xs border transition-colors",
                      isSelected
                        ? "bg-zinc-700 border-zinc-500 text-zinc-100"
                        : "bg-transparent border-zinc-700 text-zinc-400"
                    )}
                  >
                    <div className={cn(
                      "w-1.5 h-1.5 rounded-full shrink-0",
                      isLive ? "bg-green-500" : "bg-zinc-600"
                    )} />
                    <span className="max-w-[100px] truncate">{title}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowNewSheet(true)}
              className="shrink-0 text-zinc-400 hover:text-zinc-200 p-1"
              title="New session"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {sessionForHeader ? (
          <>
            <SessionHeader
              session={sessionForHeader}
              onEnd={handleEnd}
              onDelete={() => handleDelete(activeSessionId!)}
            />
            <SessionTerminal
              ref={setPullRef}
              messages={messages}
              isAgentWorking={isAgentWorking}
              pullIndicatorSlot={
                <PullToRefreshIndicator
                  pullDistance={pullDistance}
                  isRefreshing={isRefreshing}
                  isPastThreshold={isPastThreshold}
                />
              }
            />
            {/* Dead session banner */}
            {isSessionDead && (
              <div className="px-3 py-1.5 bg-zinc-900 border-t border-zinc-800 text-center text-xs text-zinc-500">
                Session ended · send a message to reactivate with history
              </div>
            )}
            <SessionInput
              onSend={handleSend}
              disabled={isAgentWorking}
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
            {isLoading ? (
              <p className="text-sm text-zinc-500">Loading sessions...</p>
            ) : (
              <>
                <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                  <Terminal className="h-8 w-8 text-zinc-600" />
                </div>
                <h2 className="text-lg font-semibold text-zinc-300">
                  Live Agent Sessions
                </h2>
                <p className="text-sm text-zinc-500 text-center max-w-xs">
                  Start a coding session with an AI agent. Watch it work in real
                  time.
                </p>
                <Button onClick={() => setShowNewSheet(true)} className="gap-2">
                  <Terminal className="h-4 w-4" />
                  Start a Session
                </Button>
              </>
            )}
          </div>
        )}

        {/* Bottom nav (mobile) */}
        <BottomNav />
      </div>

      {/* New session sheet */}
      <NewSessionSheet
        open={showNewSheet}
        onClose={() => setShowNewSheet(false)}
        onCreate={handleCreate}
        isCreating={createSession.isPending}
      />
    </div>
  );
}
